import { useState } from "react";
import { Typography, Card, Box, Divider } from "../../../../components";
import RenderizadorCamposValues from "./renderizador-campos-values.component";
import { PuxaPlacaService, notification } from "../../../../services";
import { ValidarPlacaHelper } from "../../../../helpers";
import {
  isAtivoVeiculo,
  isCampoPlaca,
  isCampoVeiculo,
} from "../../entities/campos-veiculo";

const puxaPlacaService = new PuxaPlacaService();
const validarPlaca = new ValidarPlacaHelper();

const AtivosOrdemServico = ({
  ordemServicoEntity,
  setOrdemServico,
  ordemServicos = [],
  isFechado,
  somenteLeitura = false,
}) => {
  const [buscandoPlaca, setBuscandoPlaca] = useState(false);
  const [lancamentoManual, setLancamentoManual] = useState({});

  const alternarLancamentoManual = (ativoId) =>
    setLancamentoManual((prev) => ({ ...prev, [ativoId]: !prev[ativoId] }));

  const handleUpdateValorCampo = (ativoId, campoPersonalizadoId, novoValor) => {
    ordemServicoEntity.atualizarValorCampoAtivo(
      ativoId,
      campoPersonalizadoId,
      novoValor,
    );
    setOrdemServico({ ...ordemServicoEntity });
  };

  const buscarNoHistorico = (ativo, placaNormalizada) => {
    const placaCampo = ativo.camposPersonalizados?.find(isCampoPlaca);
    if (!placaCampo) return null;
    const idsVeiculo = new Set(
      (ativo.camposPersonalizados || [])
        .filter(isCampoVeiculo)
        .map((campo) => campo.id),
    );
    for (const os of ordemServicos) {
      for (const ativoPassado of os.ativos || []) {
        if (ativoPassado.ativoId !== ativo.ativoId) continue;
        const placaPassada = ativoPassado.valoresCampos?.find(
          (campo) => campo.campoPersonalizadoId === placaCampo.id,
        );
        if (
          placaPassada &&
          validarPlaca.normalizar(placaPassada.valor) === placaNormalizada
        ) {
          return (ativoPassado.valoresCampos || []).filter((campo) =>
            idsVeiculo.has(campo.campoPersonalizadoId),
          );
        }
      }
    }
    return null;
  };

  const preencherCampos = (ativoId, campos) => {
    campos.forEach((campo) =>
      ordemServicoEntity.atualizarValorCampoAtivo(
        ativoId,
        campo.campoPersonalizadoId,
        campo.valor,
      ),
    );
    setOrdemServico({ ...ordemServicoEntity });
  };

  const handleBuscarPorPlaca = async (ativo) => {
    if (!ordemServicoEntity.clienteId) {
      notification.alertaGenericos(
        "Selecione um cliente antes de buscar por placa.",
      );
      return;
    }
    const campoPlaca = ativo.camposParaRenderizar?.find(isCampoPlaca);
    const placa = campoPlaca?.value;
    if (!validarPlaca.validar(placa)) {
      notification.alertaGenericos(
        "Informe uma placa válida (formato antigo ou Mercosul) antes de buscar.",
      );
      return;
    }
    const placaNormalizada = validarPlaca.normalizar(placa);

    const historico = buscarNoHistorico(ativo, placaNormalizada);
    if (historico?.length) {
      preencherCampos(ativo.ativoId, historico);
      notification.sucessoGenericos(
        "Dados do veículo preenchidos pelo histórico do cliente.",
      );
      return;
    }

    setBuscandoPlaca(true);
    const result = await puxaPlacaService.consultarPlaca(placaNormalizada);
    setBuscandoPlaca(false);
    if (result.isAxiosError) {
      const status = result.response?.status;
      if (status === 404) {
        notification.alertaGenericos("Placa não encontrada na base de consulta.");
      } else if (status === 401 || status === 403) {
        notification.erroGenericos(
          "Falha na autenticação com o serviço de placas. Verifique o token configurado.",
        );
      } else if (status === 429) {
        notification.alertaGenericos(
          result.response?.data?.message ||
            "Aguarde alguns segundos antes de consultar outra placa.",
        );
      } else {
        notification.erroGenericos(
          "Não foi possível consultar a placa no momento. Tente novamente.",
        );
      }
      return;
    }
    const dados = result.data?.basico?.dados;
    if (!dados) {
      notification.alertaGenericos("Placa não encontrada na base de consulta.");
      return;
    }
    ordemServicoEntity.preencherAtivoPorPlaca(ativo.ativoId, result.data);
    setOrdemServico({ ...ordemServicoEntity });
    notification.sucessoGenericos("Dados do veículo preenchidos pela placa.");
  };

  const ativosDaEntidade = ordemServicoEntity.ativos || [];

  if (ativosDaEntidade.length === 0) {
    return (
      <>
        <Divider sx={{ my: 2, border: "1px solid grey", width: "100%" }} />
        <Typography
          variant="body2"
          color="textSecondary"
          align="center"
          sx={{ my: 2 }}
        >
          Nenhum ativo vinculado a esta ordem de serviço.
        </Typography>
        <Divider sx={{ my: 2, border: "1px solid grey", width: "100%" }} />
      </>
    );
  }

  return (
    <>
      <Divider sx={{ my: 2, border: "1px solid grey", width: "100%" }} />
      <Box sx={{ maxHeight: "500px", overflowY: "auto" }}>
        {ativosDaEntidade.map((ativo) => {
          const isVeiculo = isAtivoVeiculo(ativo);
          const bloquearAtivo =
            somenteLeitura || (isVeiculo && !!ordemServicoEntity.ativosCopiados);
          const permiteManual =
            isVeiculo && ativo.permiteLancamentoManual && !bloquearAtivo;
          const emLancamentoManual =
            permiteManual && !!lancamentoManual[ativo.ativoId];
          return (
            <Card key={ativo.ativoId} variant="outlined">
              <>
                <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                  Dados do Ativo - {ativo.nome}
                </Typography>
                <Divider sx={{ borderColor: "#f0f0f0" }} />
                {ativo.camposParaRenderizar &&
                ativo.camposParaRenderizar.length ? (
                  <RenderizadorCamposValues
                    campos={ativo.camposParaRenderizar}
                    onChangeValor={(campoId, novoValor) =>
                      handleUpdateValorCampo(ativo.ativoId, campoId, novoValor)
                    }
                    disabled={isFechado || bloquearAtivo}
                    onBuscarPlaca={
                      bloquearAtivo
                        ? undefined
                        : () => handleBuscarPorPlaca(ativo)
                    }
                    buscandoPlaca={buscandoPlaca}
                    lancamentoManual={emLancamentoManual}
                    onAlternarLancamentoManual={
                      permiteManual
                        ? () => alternarLancamentoManual(ativo.ativoId)
                        : undefined
                    }
                  />
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontStyle: "italic",
                      display: "block",
                      textAlign: "center",
                    }}
                  >
                    Este ativo não possui campos personalizados configurados.
                  </Typography>
                )}
              </>
            </Card>
          );
        })}
      </Box>
      <Divider sx={{ mt: 2, border: "1px solid grey", width: "100%" }} />
    </>
  );
};

export default AtivosOrdemServico;
